import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Botao, Modal } from '../../ui';

type Ponto = { x: number; y: number };
/** Um traço é a sequência de pontos de um toque/clique contínuo. */
type Traco = Ponto[];

export interface MarcarAvariaCanvasProps {
  /** Imagem de base — sempre a ORIGINAL, para o traço não acumular ao reeditar. */
  imagemUrl: string;
  legenda?: string;
  onCancelar: () => void;
  /** Recebe a imagem já com os traços queimados. */
  onConfirmar: (arquivo: File) => void;
}

const COR_TRACO = '#E02020';

/**
 * Editor de marcação de avaria: o consultor risca sobre a foto onde está o dano.
 *
 * Os traços são guardados em coordenadas da imagem em RESOLUÇÃO NATURAL (não da
 * tela), e redesenhados a cada mudança — é isso que permite Desfazer sem borracha
 * e garante que o traço fique no lugar certo na imagem final, que costuma ser
 * muito maior que a área exibida no celular.
 *
 * 🔴 **A LÓGICA DE DESENHO NÃO FOI TOCADA na at-63 (D2)** — `redesenhar`,
 * `espessura`, `posicaoNaImagem`, os handlers de ponteiro e o `toBlob` JPEG 0.9
 * estão verbatim. O que mudou: o overlay manual (`.avaria-overlay`/`.avaria-modal`)
 * virou o `Modal` do kit, que traz Esc, clique fora e **foco preso** — coisas que
 * o overlay não fazia apesar de declarar `aria-modal="true"` —, e os botões
 * viraram `Botao`.
 *
 * ⚠️ **`tamanho="lg"`** e o `touch-action: none` no canvas são obrigatórios: sem o
 * segundo, o dedo **rola a página** em vez de desenhar no celular.
 */
const MarcarAvariaCanvas: React.FC<MarcarAvariaCanvasProps> = ({
  imagemUrl,
  legenda,
  onCancelar,
  onConfirmar,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imagemRef = useRef<HTMLImageElement | null>(null);
  const tracosRef = useRef<Traco[]>([]);
  const desenhandoRef = useRef(false);

  const [carregada, setCarregada] = useState(false);
  const [erro, setErro] = useState('');
  const [quantidadeTracos, setQuantidadeTracos] = useState(0);
  const [salvando, setSalvando] = useState(false);

  // Espessura proporcional à imagem: 4px numa foto de 3000px de largura seria
  // invisível; 0,5% da largura mantém o traço visível em qualquer resolução.
  const espessura = useCallback(() => {
    const img = imagemRef.current;
    if (!img) return 6;
    return Math.max(4, Math.round(img.naturalWidth * 0.005));
  }, []);

  const redesenhar = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imagemRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = COR_TRACO;
    ctx.lineWidth = espessura();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    tracosRef.current.forEach((traco) => {
      if (traco.length === 0) return;

      ctx.beginPath();
      if (traco.length === 1) {
        // Toque único: um ponto (círculo), senão nada apareceria.
        ctx.arc(traco[0].x, traco[0].y, espessura() / 2, 0, Math.PI * 2);
        ctx.fillStyle = COR_TRACO;
        ctx.fill();
        return;
      }

      ctx.moveTo(traco[0].x, traco[0].y);
      traco.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    });
  }, [espessura]);

  useEffect(() => {
    const img = new Image();
    // A foto vem de blob: (captura local), mas se algum dia vier do GCS o canvas
    // precisa de CORS liberado para não "sujar" e bloquear o toBlob.
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      imagemRef.current = img;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }
      setCarregada(true);
      redesenhar();
    };

    img.onerror = () => setErro('Não foi possível carregar a foto para marcação.');
    img.src = imagemUrl;
  }, [imagemUrl, redesenhar]);

  /** Converte a posição do ponteiro na tela para coordenada da imagem natural. */
  const posicaoNaImagem = (evento: React.PointerEvent<HTMLCanvasElement>): Ponto | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const escalaX = canvas.width / rect.width;
    const escalaY = canvas.height / rect.height;

    return {
      x: (evento.clientX - rect.left) * escalaX,
      y: (evento.clientY - rect.top) * escalaY,
    };
  };

  const iniciarTraco = (evento: React.PointerEvent<HTMLCanvasElement>) => {
    if (!carregada) return;
    const ponto = posicaoNaImagem(evento);
    if (!ponto) return;

    // Captura o ponteiro para o traço continuar mesmo se o dedo sair do canvas.
    evento.currentTarget.setPointerCapture(evento.pointerId);
    desenhandoRef.current = true;
    tracosRef.current = [...tracosRef.current, [ponto]];
    setQuantidadeTracos(tracosRef.current.length);
    redesenhar();
  };

  const continuarTraco = (evento: React.PointerEvent<HTMLCanvasElement>) => {
    if (!desenhandoRef.current) return;
    const ponto = posicaoNaImagem(evento);
    if (!ponto) return;

    const tracos = tracosRef.current;
    const atual = tracos[tracos.length - 1];
    if (!atual) return;

    atual.push(ponto);
    redesenhar();
  };

  const encerrarTraco = () => {
    desenhandoRef.current = false;
  };

  const desfazer = () => {
    tracosRef.current = tracosRef.current.slice(0, -1);
    setQuantidadeTracos(tracosRef.current.length);
    redesenhar();
  };

  const limpar = () => {
    tracosRef.current = [];
    setQuantidadeTracos(0);
    redesenhar();
  };

  const confirmar = () => {
    const canvas = canvasRef.current;
    if (!canvas || salvando) return;

    setSalvando(true);
    // O canvas já está na resolução natural com os traços aplicados — o blob que
    // sai daqui é exatamente a imagem que vai ao GCS, ao PDF e ao SGA.
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setErro('Não foi possível gerar a imagem marcada. Tente novamente.');
          setSalvando(false);
          return;
        }

        const arquivo = new File([blob], `vistoria-avaria-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });
        onConfirmar(arquivo);
      },
      'image/jpeg',
      0.9
    );
  };

  return (
    <Modal
      /* O pai renderiza este componente condicionalmente, então quando ele existe
         o diálogo está aberto. */
      aberto
      titulo="Marcar avaria"
      descricao={
        legenda
          ? `${legenda} — risque sobre a foto onde está a avaria.`
          : 'Risque sobre a foto onde está a avaria.'
      }
      tamanho="lg"
      ocupado={salvando}
      aoFechar={onCancelar}
      rodape={
        <>
          <Botao
            variante="primario"
            carregando={salvando}
            disabled={!carregada || !quantidadeTracos || salvando}
            title={!quantidadeTracos ? 'Faça pelo menos um traço para marcar a avaria.' : undefined}
            onClick={confirmar}
          >
            {salvando ? 'Salvando...' : 'Confirmar avaria'}
          </Botao>
          <Botao
            variante="secundario"
            disabled={!quantidadeTracos || salvando}
            onClick={desfazer}
          >
            Desfazer
          </Botao>
          <Botao
            variante="secundario"
            disabled={!quantidadeTracos || salvando}
            onClick={limpar}
          >
            Limpar
          </Botao>
          <Botao variante="fantasma" disabled={salvando} onClick={onCancelar}>
            Cancelar
          </Botao>
        </>
      }
    >
      {erro ? (
        <p className="tw-m-0 tw-py-6 tw-text-center tw-text-sm tw-font-semibold tw-text-danger">
          {erro}
        </p>
      ) : (
        <div className="tw-relative tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#2b2b2b] tw-p-2">
          <canvas
            ref={canvasRef}
            onPointerDown={iniciarTraco}
            onPointerMove={continuarTraco}
            onPointerUp={encerrarTraco}
            onPointerLeave={encerrarTraco}
            onPointerCancel={encerrarTraco}
            /* ⚠️ O canvas é desenhado em resolução NATURAL; aqui só a exibição é
               escalada. `h-auto` preserva a proporção — sem isso o traço sai
               deslocado do lugar onde o dedo tocou.
               ⚠️ `touch-none` é obrigatório: sem ele o dedo ROLA A PÁGINA em vez
               de desenhar no celular. */
            className="tw-block tw-h-auto tw-max-h-[55vh] tw-max-w-full tw-cursor-crosshair tw-touch-none tw-bg-black sm:tw-max-h-[60vh]"
          />
          {!carregada && (
            <p className="tw-absolute tw-m-0 tw-text-sm tw-text-white">Carregando foto...</p>
          )}
        </div>
      )}
    </Modal>
  );
};

export default MarcarAvariaCanvas;
